import { track } from '@adamdotai/core/analytics'
import {
    ActionsIcon,
    AgendaIcon,
    DecisionsIcon,
    DeleteIcon,
    Divider,
    EditIcon,
    Flex,
    IconButton,
    NotesIcon,
    PollIcon,
    useToast,
} from '@adamdotai/design-system'
import {
    INSERT_ACTION_COMMAND,
    INSERT_AGENDA_COMMAND,
    INSERT_DECISION_COMMAND,
    OPEN_CREATE_POLL_COMMAND,
} from '@adamdotai/editor/ContentItemPlugin'
import {
    $createAdamHeadingNode,
    $createAdamParagraphNode,
    INSERT_ADAM_NOTE_COMMAND,
} from '@adamdotai/editor/NotesPlugin'
import type { StrategyBasedInsertContentItemSource } from '@adamdotai/editor/types'
import { $createTextNode, $getRoot, type LexicalEditor } from 'lexical'
import { useTranslations } from 'next-intl'
import type React from 'react'
import { memo, useMemo } from 'react'

const handleEditorNodesInsertion = (
    itemText: string,
    itemType: string,
    editorReference: React.RefObject<LexicalEditor | undefined>,
) => {
    const editor = editorReference.current
    editor.getEditorState().read(() => {
        const lastNodeKey = $getRoot().getLastChild().getKey()
        const source: StrategyBasedInsertContentItemSource = {
            type: 'strategy-based',
            insertStrategy: 'insert-after',
            nodeKey: lastNodeKey,
        }

        if (itemType.toUpperCase() === 'ACTION') {
            editor.dispatchCommand(INSERT_ACTION_COMMAND, {
                source,
                payload: { title: itemText },
            })
        } else if (itemType.toUpperCase() === 'AGENDA') {
            editor.dispatchCommand(INSERT_AGENDA_COMMAND, {
                source,
                payload: { name: itemText },
            })
        } else if (itemType.toUpperCase() === 'DECISION') {
            editor.dispatchCommand(INSERT_DECISION_COMMAND, {
                source,
                payload: { title: itemText },
            })
        } else if (itemType.toUpperCase() === 'NOTE') {
            editor.dispatchCommand(INSERT_ADAM_NOTE_COMMAND, {
                source,
                createNote: () => {
                    const node = $createAdamParagraphNode()
                    const text = $createTextNode(itemText)
                    node.append(text)
                    return node
                },
            })
        } else if (itemType.toUpperCase() === 'HEADING') {
            editor.dispatchCommand(INSERT_ADAM_NOTE_COMMAND, {
                source,
                createNote: () => {
                    const node = $createAdamHeadingNode('h1')
                    const text = $createTextNode(itemText)
                    node.append(text)
                    return node
                },
            })
        } else if (itemType.toUpperCase() === 'POLL') {
            editor.dispatchCommand(OPEN_CREATE_POLL_COMMAND, {
                source,
                payload: {
                    question: itemText,
                },
            })
        }
    })
}

interface CommandsToolbarProps {
    content: string
    editorReference: React.MutableRefObject<LexicalEditor | undefined>
    onEdit: () => void
    onDelete: () => void
    addItemTrackingEvent: string
}

const CommandsToolbar = memo<CommandsToolbarProps>(
    ({ content, editorReference, onEdit, onDelete, addItemTrackingEvent }) => {
        const t = useTranslations()
        const toast = useToast()

        const toolbarCommands = useMemo(
            () => [
                {
                    icon: <ActionsIcon />,
                    label: t('meetingroom.add-as-action'),
                    type: 'action',
                },
                {
                    icon: <AgendaIcon />,
                    label: t('meetingroom.add-as-agenda'),
                    type: 'agenda',
                },
                {
                    icon: <DecisionsIcon />,
                    label: t('meetingroom.add-as-decision'),
                    type: 'decision',
                },
                {
                    icon: <PollIcon />,
                    label: t('meetingroom.add-as-poll'),
                    type: 'poll',
                },
                {
                    icon: <NotesIcon />,
                    label: t('meetingroom.add-as-notes'),
                    type: 'note',
                },
                {
                    label: 'Divider',
                    type: 'divider',
                },
                {
                    icon: <EditIcon />,
                    label: t('common.edit'),
                    type: 'edit',
                },
                {
                    // Fix color red
                    icon: (
                        <DeleteIcon
                            style={{
                                color: 'red',
                            }}
                        />
                    ),
                    label: t('common.delete'),
                    type: 'delete',
                },
            ],
            [t],
        )

        const handleAction = (type: string) => {
            switch (type) {
                case 'edit':
                    onEdit()
                    break
                case 'delete':
                    onDelete()
                    break
                default:
                    track(addItemTrackingEvent)
                    handleEditorNodesInsertion(content, type, editorReference)
                    // don't show toast for poll since it's a 2-step process of opening the modal and then submitting the poll
                    if (type !== 'poll') {
                        toast({
                            status: 'success',
                            content: t('toast.content-added-successfully'),
                        })
                    }
                    break
            }
        }

        return (
            <Flex
                gap="1"
                py="1"
                px="2"
                borderRadius={4}
                h="10"
                position="absolute"
                top="-10"
                insetEnd="5"
                bg="white"
                justifyContent="center"
                alignItems="center"
                border="1px solid"
                borderColor="neutral.500"
                boxShadow="md"
            >
                {toolbarCommands.map((command) =>
                    command.type !== 'divider' ? (
                        <IconButton
                            key={command.type}
                            tooltipProps={{
                                label: command.label,
                                placement: 'bottom-start',
                            }}
                            onClick={() => handleAction(command.type)}
                        >
                            {command.icon}
                        </IconButton>
                    ) : (
                        <Divider
                            key={command.type}
                            orientation="vertical"
                            h="24px"
                            my="2"
                        />
                    ),
                )}
            </Flex>
        )
    },
)
CommandsToolbar.displayName = 'CommandsToolbar'

export { CommandsToolbar }
